import { CatalogOffering } from "@/data/catalog";

import { Suspense } from "react";

import {
  isPartnerCatalogCategory,
  PartnerCatalog,
  PartnerCatalogBlock,
  PartnerCatalogCategory,
} from "@/data/catalog";
import {
  CatalogOfferingCardV2,
  CatalogOfferingTargetUrlCardV2,
} from "../more_to_explore";
import { Container } from "@/components";
import { Grid } from "@/offering/grid";
import React from "react";
import { PulseLoader } from "@/components/pulse_loader";
import { useQueryString } from "@/hooks";
import { Spacer } from "@/offering/spacer";
import { FilterLocation } from "@/data/partner_offering";

interface FilteredCatalogProps {
  catalogSlug?: string;
  catalog: PartnerCatalog;
  locations: FilterLocation[];
  catalogBlocks?: PartnerCatalogBlock[];
}

const filterCategoryOfferings = (
  locationID: string,
  categories: PartnerCatalogCategory[]
) => {
  return categories.map((category) => {
    return {
      ...category,
      catalogOfferings: category.catalogOfferings?.filter((offering) =>
        offering.locationIDs.includes(locationID)
      ),
    };
  });
};

const getAllOfferingsFromFilteredCategories = (
  categories: PartnerCatalogCategory[],
  locationID: string
) => {
  return filterCategoryOfferings(locationID, categories).flatMap(
    (category) => category.catalogOfferings || []
  );
};

const filterCatalogBlocks = (
  locationID: string,
  catalogBlocks: PartnerCatalogBlock[]
): PartnerCatalogBlock[] => {
  return catalogBlocks.map((block) => {
    return {
      ...block,
      item: isPartnerCatalogCategory(block.item)
        ? {
            ...block.item,
            catalogOfferings: block.item?.catalogOfferings?.filter((offering) =>
              offering.locationIDs.includes(locationID)
            ),
          }
        : block.item,
    };
  });
};

const getAllOfferingsFromFilteredBlocks = (
  catalogBlocks: PartnerCatalogBlock[],
  locationID: string
): CatalogOffering[] => {
  return filterCatalogBlocks(locationID, catalogBlocks).flatMap((block) =>
    isPartnerCatalogCategory(block.item)
      ? block.item.catalogOfferings || []
      : []
  );
};
export const FilteredCatalog = (props: FilteredCatalogProps) => {
  const { catalog, catalogSlug, locations, catalogBlocks } = props;
  const { locationID } = useQueryString();

  const offerings = catalogBlocks
    ? getAllOfferingsFromFilteredBlocks(catalogBlocks, locationID)
    : getAllOfferingsFromFilteredCategories(
        catalog.partnerCatalogCategories,
        locationID
      );

  const selectedLocation = locations?.find(
    (location) => location.id === locationID
  );

  const title = `${offerings.length} offerings at ${selectedLocation?.locationDisplay}`;

  if (!selectedLocation) {
    return null;
  }

  return (
    <Container className="mb-5 filtered-catalog">
      <div>
        <h3>{title}</h3>
        <Spacer size={16} />
      </div>
      <Grid itemsPerRow={{ md: 3, default: 1 }} spacing={32}>
        {offerings?.map((o, index) => {
          const {
            targetUrl,
            title,
            image,
            description,
            slug,
            comingSoon,
            membership,
            ctaLabel,
          } = o;

          return (
            <div
              key={o.id}
              data-testid="flexible-template-catalog-offering-item"
              className="h-100"
            >
              <Container key={title} paddingBottom={32} className="h-100">
                <Suspense
                  fallback={
                    <Container minHeight={240} center>
                      <PulseLoader />
                    </Container>
                  }
                >
                  {!targetUrl ? (
                    <CatalogOfferingCardV2
                      key={index}
                      image={image}
                      title={title}
                      description={description}
                      offeringSlug={slug}
                      catalogSlug={catalogSlug}
                      comingSoon={comingSoon}
                      membership={membership}
                      ctaLabel={ctaLabel}
                    />
                  ) : (
                    <CatalogOfferingTargetUrlCardV2
                      key={index}
                      image={image}
                      title={title}
                      description={description}
                      comingSoon={comingSoon}
                      targetUrl={targetUrl}
                      membership={membership}
                      ctaLabel={ctaLabel}
                    />
                  )}
                </Suspense>
              </Container>
            </div>
          );
        })}
      </Grid>
    </Container>
  );
};
